import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import apiToken, {
  handleApiError,
  isSuccess,
} from '@/shared/services/axiosInstance';
import { VALIDATION_MESSAGES } from '@/shared/utils/validator';
import { useAuthStore } from '@/shared/stores/useAuthStore';
import { useLoginValidate } from './useLoginValidate';

export const useLogin = () => {
  const { validateEmail, validatePassword } = useLoginValidate();
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  const setToken = useAuthStore((state) => state.setToken);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);

    if (emailError || passwordError) {
      setErrors({
        email: '',
        password: VALIDATION_MESSAGES.login.invalidCredentials,
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiToken.post('/api/v1/auth/login', formData);
      const { data } = response;

      if (isSuccess(data) && data.data.accessToken) {
        setToken(data.data.accessToken);
        // Redirect or handle success
        navigate('/');
      } else {
        setErrors({
          email: data.message || '',
          password: 'Login failed',
        });
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      setErrors({
        email: '',
        password: errorMessage || 'Login failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { formData, errors, isLoading, handleInputChange, handleSubmit };
};
